import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  CheckCircle as PaidIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Description as InvoiceIcon,
  AccountBalance as MoneyIcon,
  Timer as TimerIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  SaveAlt as DownloadIcon,
} from '@mui/icons-material';
import { abrechnungApi, zeiterfassungApi, adressenApi } from '../services/api';
import type {
  Abrechnung,
  AbrechnungStatistik,
  CreateAbrechnungDto,
  Zeiterfassung,
  Adresse,
  SendRechnungEmailDto,
} from '../types/api';

const AbrechnungPage: React.FC = () => {
  const [abrechnungen, setAbrechnungen] = useState<Abrechnung[]>([]);
  const [statistik, setStatistik] = useState<AbrechnungStatistik | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('alle');

  // Create Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Detail Dialog State
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAbrechnung, setSelectedAbrechnung] = useState<Abrechnung | null>(null);

  // Lookup Data
  const [adressen, setAdressen] = useState<Adresse[]>([]);
  const [freigegebeneZeiterfassungen, setFreigegebeneZeiterfassungen] = useState<Zeiterfassung[]>([]);

  // Form Data
  const [formData, setFormData] = useState<CreateAbrechnungDto>({
    adresseID: 0,
    vonDatum: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    bisDatum: new Date().toISOString().split('T')[0],
    abrechnungsart: 'Stundensatz',
    zahlungszielTage: 14,
    mwStProzent: 8.8,
  });
  const [selectedZeiterfassungen, setSelectedZeiterfassungen] = useState<number[]>([]);

  // Email Dialog State
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAbrechnung, setEmailAbrechnung] = useState<Abrechnung | null>(null);
  const [emailFormData, setEmailFormData] = useState<SendRechnungEmailDto>({
    empfaengerEmail: '',
    betreff: '',
    nachricht: '',
    cc: '',
  });
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    loadData();
    loadLookupData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [abrechnungenRes, statRes] = await Promise.all([
        abrechnungApi.getAll({ take: 500 }),
        abrechnungApi.getStatistik(),
      ]);

      setAbrechnungen(abrechnungenRes.data);
      setStatistik(statRes.data);
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Abrechnungen');
    } finally {
      setLoading(false);
    }
  };

  const loadLookupData = async () => {
    try {
      const adressenRes = await adressenApi.getAll();
      // Nur Kunden (Mandanten) anzeigen, keine internen Adressen
      setAdressen(adressenRes.data.filter((a) => a.istMandant));
    } catch (err) {
      console.error('Fehler beim Laden der Lookup-Daten:', err);
    }
  };

  const loadFreigegebeneZeiterfassungen = async (adresseID: number) => {
    try {
      const res = await zeiterfassungApi.getAll({
        adresseID,
        status: 'Freigegeben',
        abrechenbar: true,
      });
      setFreigegebeneZeiterfassungen(res.data);
    } catch (err) {
      console.error('Fehler beim Laden der Zeiterfassungen:', err);
    }
  };

  const filteredData = useMemo(() => {
    let result = [...abrechnungen];

    // Textsuche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (a) =>
          a.rechnungsnummer.toLowerCase().includes(term) ||
          (a.adresseName || '').toLowerCase().includes(term) ||
          (a.betreff || '').toLowerCase().includes(term)
      );
    }

    // Status-Filter
    if (statusFilter !== 'alle') {
      if (statusFilter === 'ueberfaellig') {
        result = result.filter((a) => a.istUeberfaellig);
      } else {
        result = result.filter((a) => a.status === statusFilter);
      }
    }

    // Nach Datum sortieren (neueste zuerst)
    result.sort((a, b) => new Date(b.erstelltAm).getTime() - new Date(a.erstelltAm).getTime());

    return result;
  }, [abrechnungen, searchTerm, statusFilter]);

  const handleOpenCreateDialog = () => {
    setFormData({
      adresseID: 0,
      vonDatum: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      bisDatum: new Date().toISOString().split('T')[0],
      abrechnungsart: 'Stundensatz',
      zahlungszielTage: 14,
      mwStProzent: 8.8,
    });
    setSelectedZeiterfassungen([]);
    setFreigegebeneZeiterfassungen([]);
    setActiveStep(0);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const handleNext = () => {
    if (activeStep === 0 && formData.adresseID) {
      loadFreigegebeneZeiterfassungen(formData.adresseID);
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreateAbrechnung = async () => {
    try {
      const data: CreateAbrechnungDto = {
        ...formData,
        zeiterfassungIds: formData.abrechnungsart === 'Stundensatz' ? selectedZeiterfassungen : undefined,
      };
      await abrechnungApi.create(data);
      handleCloseCreateDialog();
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Erstellen:', err);
      alert(err.response?.data?.message || 'Fehler beim Erstellen der Abrechnung');
    }
  };

  const handleViewDetails = async (id: number) => {
    try {
      const res = await abrechnungApi.getById(id);
      setSelectedAbrechnung(res.data);
      setDetailDialogOpen(true);
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
    }
  };

  const handleVersenden = async (id: number) => {
    if (!window.confirm('Rechnung als versendet markieren?')) return;
    try {
      await abrechnungApi.versenden(id);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Versenden');
    }
  };

  const handleBezahlt = async (id: number) => {
    if (!window.confirm('Rechnung als bezahlt markieren?')) return;
    try {
      await abrechnungApi.bezahlt(id);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler');
    }
  };

  const handleStornieren = async (id: number) => {
    const grund = window.prompt('Grund für Stornierung:');
    if (!grund) return;
    try {
      await abrechnungApi.stornieren(id, grund);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Stornieren');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Entwurf wirklich löschen?')) return;
    try {
      await abrechnungApi.delete(id);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  // PDF & Email Handlers
  const handleGenerateDokument = async (id: number) => {
    try {
      const res = await abrechnungApi.generateDokument(id);
      alert(`PDF erstellt: ${res.data.betreff}\nDokument-ID: ${res.data.dokumentID}`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Erstellen des PDFs');
    }
  };

  const handleDownloadPdf = async (id: number, rechnungsnummer: string) => {
    try {
      const res = await abrechnungApi.downloadPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rechnung_${rechnungsnummer.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Herunterladen des PDFs');
    }
  };

  const handleOpenEmailDialog = (abrechnung: Abrechnung) => {
    setEmailAbrechnung(abrechnung);
    setEmailFormData({
      empfaengerEmail: abrechnung.adresseEmail || '',
      betreff: `Rechnung ${abrechnung.rechnungsnummer}`,
      nachricht: `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie unsere Rechnung ${abrechnung.rechnungsnummer}.\n\nMit freundlichen Grüssen`,
      cc: '',
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailAbrechnung) return;
    if (!emailFormData.empfaengerEmail) {
      alert('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    try {
      setEmailSending(true);
      await abrechnungApi.sendEmail(emailAbrechnung.abrechnungID, emailFormData);
      alert('E-Mail erfolgreich versendet');
      setEmailDialogOpen(false);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Versenden der E-Mail');
    } finally {
      setEmailSending(false);
    }
  };

  const getStatusColor = (status: string, istUeberfaellig: boolean) => {
    if (istUeberfaellig) return 'error';
    switch (status) {
      case 'Entwurf':
        return 'default';
      case 'Versendet':
        return 'primary';
      case 'Bezahlt':
        return 'success';
      case 'Storniert':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatBetrag = (betrag: number) => {
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(betrag);
  };

  const toggleZeiterfassung = (id: number) => {
    setSelectedZeiterfassungen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const berechnetesSumme = useMemo(() => {
    const selected = freigegebeneZeiterfassungen.filter((z) =>
      selectedZeiterfassungen.includes(z.zeiterfassungID)
    );
    return selected.reduce((sum, z) => sum + (z.betrag || 0), 0);
  }, [freigegebeneZeiterfassungen, selectedZeiterfassungen]);

  const steps = ['Kunde wählen', 'Zeiterfassungen auswählen', 'Zusammenfassung'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon /> Abrechnung
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
            Aktualisieren
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            Neue Rechnung
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistik-Karten */}
      {statistik && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Umsatz (Monat)
                    </Typography>
                    <Typography variant="h5">{formatBetrag(statistik.umsatzDieserMonat)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="success" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Umsatz (Jahr)
                    </Typography>
                    <Typography variant="h5">{formatBetrag(statistik.umsatzDiesesJahr)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InvoiceIcon color="warning" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Offene Beträge
                    </Typography>
                    <Typography variant="h5">{formatBetrag(statistik.offeneBetrage)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="error" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Überfällig
                    </Typography>
                    <Typography variant="h5" color="error">
                      {formatBetrag(statistik.ueberfaelligeBetrage)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Suchen (Nummer, Kunde, Betreff)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="alle">Alle</MenuItem>
                <MenuItem value="Entwurf">Entwurf</MenuItem>
                <MenuItem value="Versendet">Versendet</MenuItem>
                <MenuItem value="Bezahlt">Bezahlt</MenuItem>
                <MenuItem value="ueberfaellig">Überfällig</MenuItem>
                <MenuItem value="Storniert">Storniert</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabelle */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rechnungsnummer</TableCell>
              <TableCell>Kunde</TableCell>
              <TableCell>Zeitraum</TableCell>
              <TableCell align="right">Brutto</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Fällig</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Keine Rechnungen gefunden
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.abrechnungID} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {item.rechnungsnummer}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.abrechnungsart}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.adresseName}</Typography>
                    {item.betreff && (
                      <Typography variant="caption" color="text.secondary">
                        {item.betreff}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(item.vonDatum).toLocaleDateString('de-DE')} -{' '}
                    {new Date(item.bisDatum).toLocaleDateString('de-DE')}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatBetrag(item.bruttobetrag)}
                    </Typography>
                    {item.gesamtStunden && (
                      <Typography variant="caption" color="text.secondary">
                        {item.gesamtStunden.toFixed(2)} h
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.istUeberfaellig ? 'Überfällig' : item.status}
                      size="small"
                      color={getStatusColor(item.status, item.istUeberfaellig)}
                    />
                  </TableCell>
                  <TableCell>
                    {item.faelligAm ? (
                      <Box>
                        <Typography variant="body2">
                          {new Date(item.faelligAm).toLocaleDateString('de-DE')}
                        </Typography>
                        {item.istUeberfaellig && (
                          <Typography variant="caption" color="error">
                            {item.tageUeberfaellig} Tage überfällig
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Details">
                      <IconButton size="small" onClick={() => handleViewDetails(item.abrechnungID)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="PDF herunterladen">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadPdf(item.abrechnungID, item.rechnungsnummer)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Per E-Mail senden">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEmailDialog(item)}
                      >
                        <EmailIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="PDF in Dokumentenverwaltung speichern">
                      <IconButton
                        size="small"
                        onClick={() => handleGenerateDokument(item.abrechnungID)}
                      >
                        <PdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {item.status === 'Entwurf' && (
                      <>
                        <Tooltip title="Als versendet markieren">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleVersenden(item.abrechnungID)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item.abrechnungID)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {item.status === 'Versendet' && (
                      <>
                        <Tooltip title="Als bezahlt markieren">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleBezahlt(item.abrechnungID)}
                          >
                            <PaidIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Stornieren">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleStornieren(item.abrechnungID)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Neue Rechnung erstellen</DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={adressen}
                  getOptionLabel={(option) => {
                    if (option.firma) return option.firma;
                    const name = [option.vorname, option.nachname].filter(Boolean).join(' ');
                    return name || `Adresse ${option.adresseID}`;
                  }}
                  value={adressen.find((a) => a.adresseID === formData.adresseID) || null}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, adresseID: newValue?.adresseID || 0 })
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Kunde" required />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Von Datum"
                  value={formData.vonDatum}
                  onChange={(e) => setFormData({ ...formData, vonDatum: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Bis Datum"
                  value={formData.bisDatum}
                  onChange={(e) => setFormData({ ...formData, bisDatum: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Abrechnungsart</InputLabel>
                  <Select
                    value={formData.abrechnungsart}
                    label="Abrechnungsart"
                    onChange={(e) => setFormData({ ...formData, abrechnungsart: e.target.value })}
                  >
                    <MenuItem value="Stundensatz">Stundensatz</MenuItem>
                    <MenuItem value="Pauschale">Pauschale</MenuItem>
                    <MenuItem value="Wertgebuehr">Wertgebühr (StBVV)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Zahlungsziel (Tage)"
                  value={formData.zahlungszielTage}
                  onChange={(e) =>
                    setFormData({ ...formData, zahlungszielTage: parseInt(e.target.value) || 14 })
                  }
                />
              </Grid>
              {formData.abrechnungsart === 'Pauschale' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pauschalbetrag (CHF)"
                    value={formData.pauschalBetrag || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, pauschalBetrag: parseFloat(e.target.value) || undefined })
                    }
                  />
                </Grid>
              )}
              {formData.abrechnungsart === 'Wertgebuehr' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Wertgebühr-Betrag (CHF)"
                      value={formData.wertgebuehrBetrag || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, wertgebuehrBetrag: parseFloat(e.target.value) || undefined })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Paragraph (z.B. §24 StBVV)"
                      value={formData.wertgebuehrParagraph || ''}
                      onChange={(e) => setFormData({ ...formData, wertgebuehrParagraph: e.target.value })}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {activeStep === 1 && formData.abrechnungsart === 'Stundensatz' && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Freigegebene Zeiterfassungen für diesen Kunden:
              </Typography>
              {freigegebeneZeiterfassungen.length === 0 ? (
                <Alert severity="info">Keine freigegebenen Zeiterfassungen gefunden</Alert>
              ) : (
                <List>
                  {freigegebeneZeiterfassungen.map((z) => (
                    <ListItem key={z.zeiterfassungID} divider>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedZeiterfassungen.includes(z.zeiterfassungID)}
                          onChange={() => toggleZeiterfassung(z.zeiterfassungID)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${new Date(z.datum).toLocaleDateString('de-DE')}: ${z.taetigkeit}`}
                        secondary={`${z.dauerStunden.toFixed(2)} h × ${formatBetrag(z.stundensatz || 0)} = ${formatBetrag(z.betrag || 0)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Ausgewählt: {selectedZeiterfassungen.length} Einträge</Typography>
                <Typography variant="h6">Summe: {formatBetrag(berechnetesSumme)}</Typography>
              </Box>
            </Box>
          )}

          {activeStep === 1 && formData.abrechnungsart !== 'Stundensatz' && (
            <Alert severity="info">
              Bei {formData.abrechnungsart}-Abrechnung werden keine Zeiterfassungen verknüpft.
            </Alert>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Zusammenfassung
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Kunde:
                  </Typography>
                  <Typography variant="body1">
                    {(() => {
                      const addr = adressen.find((a) => a.adresseID === formData.adresseID);
                      if (!addr) return '-';
                      if (addr.firma) return addr.firma;
                      const name = [addr.vorname, addr.nachname].filter(Boolean).join(' ');
                      return name || '-';
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Abrechnungsart:
                  </Typography>
                  <Typography variant="body1">{formData.abrechnungsart}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Zeitraum:
                  </Typography>
                  <Typography variant="body1">
                    {formData.vonDatum} - {formData.bisDatum}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Zahlungsziel:
                  </Typography>
                  <Typography variant="body1">{formData.zahlungszielTage} Tage</Typography>
                </Grid>
                {formData.abrechnungsart === 'Stundensatz' && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Zeiterfassungen:
                    </Typography>
                    <Typography variant="body1">
                      {selectedZeiterfassungen.length} Einträge = {formatBetrag(berechnetesSumme)} (netto)
                    </Typography>
                  </Grid>
                )}
                {formData.abrechnungsart === 'Pauschale' && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Pauschalbetrag:
                    </Typography>
                    <Typography variant="body1">{formatBetrag(formData.pauschalBetrag || 0)}</Typography>
                  </Grid>
                )}
                {formData.abrechnungsart === 'Wertgebuehr' && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Wertgebühr:
                    </Typography>
                    <Typography variant="body1">
                      {formatBetrag(formData.wertgebuehrBetrag || 0)} ({formData.wertgebuehrParagraph})
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Abbrechen</Button>
          {activeStep > 0 && <Button onClick={handleBack}>Zurück</Button>}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 && !formData.adresseID}
            >
              Weiter
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateAbrechnung}
              disabled={
                formData.abrechnungsart === 'Stundensatz' && selectedZeiterfassungen.length === 0
              }
            >
              Rechnung erstellen
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Rechnung {selectedAbrechnung?.rechnungsnummer}
        </DialogTitle>
        <DialogContent dividers>
          {selectedAbrechnung && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Kunde
                </Typography>
                <Typography variant="body1">{selectedAbrechnung.adresseName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedAbrechnung.status}
                  color={getStatusColor(selectedAbrechnung.status, selectedAbrechnung.istUeberfaellig)}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {selectedAbrechnung.positionen && selectedAbrechnung.positionen.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Positionen
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Beschreibung</TableCell>
                        <TableCell align="right">Menge</TableCell>
                        <TableCell align="right">Einzelpreis</TableCell>
                        <TableCell align="right">Betrag</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedAbrechnung.positionen.map((pos) => (
                        <TableRow key={pos.positionID}>
                          <TableCell>{pos.position}</TableCell>
                          <TableCell>{pos.beschreibung}</TableCell>
                          <TableCell align="right">
                            {pos.menge} {pos.einheit}
                          </TableCell>
                          <TableCell align="right">{formatBetrag(pos.einzelpreis || 0)}</TableCell>
                          <TableCell align="right">{formatBetrag(pos.betrag)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} sm={6}></Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">
                    Zwischensumme: {formatBetrag(selectedAbrechnung.zwischensumme)}
                  </Typography>
                  {selectedAbrechnung.rabattBetrag && (
                    <Typography variant="body2" color="success.main">
                      Rabatt ({selectedAbrechnung.rabattProzent}%): -{formatBetrag(selectedAbrechnung.rabattBetrag)}
                    </Typography>
                  )}
                  <Typography variant="body2">Netto: {formatBetrag(selectedAbrechnung.nettobetrag)}</Typography>
                  <Typography variant="body2">
                    MwSt ({selectedAbrechnung.mwStProzent}%): {formatBetrag(selectedAbrechnung.mwStBetrag)}
                  </Typography>
                  <Typography variant="h6">Brutto: {formatBetrag(selectedAbrechnung.bruttobetrag)}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rechnung per E-Mail versenden</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Rechnung: {emailAbrechnung?.rechnungsnummer}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Empfänger E-Mail"
                fullWidth
                type="email"
                value={emailFormData.empfaengerEmail}
                onChange={(e) =>
                  setEmailFormData({ ...emailFormData, empfaengerEmail: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Betreff"
                fullWidth
                value={emailFormData.betreff}
                onChange={(e) =>
                  setEmailFormData({ ...emailFormData, betreff: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nachricht"
                fullWidth
                multiline
                rows={6}
                value={emailFormData.nachricht}
                onChange={(e) =>
                  setEmailFormData({ ...emailFormData, nachricht: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="CC (optional)"
                fullWidth
                type="email"
                value={emailFormData.cc}
                onChange={(e) =>
                  setEmailFormData({ ...emailFormData, cc: e.target.value })
                }
                helperText="Mehrere E-Mail-Adressen mit ; trennen"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={emailSending || !emailFormData.empfaengerEmail}
            startIcon={emailSending ? <CircularProgress size={20} /> : <EmailIcon />}
          >
            {emailSending ? 'Wird gesendet...' : 'Senden'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AbrechnungPage;
